/**
 * 2상 유동(Two-phase Liquid/Vapor) 사이징 — Omega 법.
 *
 * 근거: API Standard 520 Part I, 10판(2020) Annex C
 *   본문 5.11.2 가 "2상 사이징 권장 방법은 Annex C 에 있다"고 지시한다.
 *
 *   C.2.2 — PRV 입구가 이미 2상인 경우 (flashing / nonflashing 모두)
 *     - 식 (C.12)  ω = 9·(v9/v1 − 1)
 *     - 식 (C.13)  Pcf = ηc·P1 ;  Pcf ≥ P2 → 임계, Pcf < P2 → 아임계
 *     - 식 (C.14)  ηc² + (ω²−2ω)(1−ηc)² + 2ω²·ln ηc + 2ω²(1−ηc) = 0   ← 정의식
 *     - 식 (C.15)  ηc 근사식
 *     - 식 (C.18)  SI 임계 질량유속 G = ηc·√(P1/(v1·ω))
 *     - 식 (C.19)  SI 아임계 질량유속
 *     - 식 (C.21)  SI 소요면적 A = 277.8·W / (Kd·Kb·Kc·Kv·G)
 *
 * 단위: 유량 kg/h · 압력 kPa(절대) · 비체적 m³/kg · 질량유속 kg/(s·m²) · 면적 mm²
 *   ※ Annex C 원문은 P 를 Pa 로 쓴다. 이 모듈은 다른 화면과 맞추려고 kPa 로 받고
 *      내부에서 Pa 로 환산한다. 식 (C.18)·(C.21)의 상수는 Pa 기준이므로 주의.
 *   ※ 압력은 **절대압**이다(기체·수증기와 동일, 액체와 반대).
 */
import type { ValveType } from "./gas";

/** 2상 예비 사이징용 유효 배출계수 (C.2.2.1 식 C.20~C.21 설명). */
export const KD_TWO_PHASE_PRELIMINARY = 0.85;

/** 식 (C.12) 에서 v9 를 평가하는 압력비. P1 의 90 %. */
export const OMEGA_FLASH_PRESSURE_RATIO = 0.9;

export interface TwoPhaseSizingInput {
  /** W — 소요 배출 유량 (kg/h) */
  readonly requiredFlowKgH: number;
  /** P1 — PRV 입구 압력 (kPa 절대). 설정압 + 허용 과압 + 대기압. */
  readonly relievingPressureKpaAbs: number;
  /** P2 — 총 배압 (kPa 절대) */
  readonly backpressureKpaAbs: number;
  /** v1 — PRV 입구에서 2상 혼합물의 비체적 (m³/kg) */
  readonly inletSpecificVolumeM3Kg: number;
  /**
   * v9 — P1 의 90 % 에서의 비체적 (m³/kg).
   * 등엔트로피 플래시로 구하는 것이 원칙이나, 임계점에서 먼 저건도 혼합물은
   * 등엔탈피(단열) 플래시로 충분하다(식 C.12 설명).
   */
  readonly flashSpecificVolumeM3Kg: number;
  readonly valveType: ValveType;
  /** Kd — 생략 시 예비 사이징값 0.85 */
  readonly dischargeCoefficient?: number;
  /** Kb — 밸런스드 벨로우즈에만 적용. 제작사 자료 또는 Figure 31. */
  readonly bellowsBackpressureCorrection?: number;
  /** Kc — 파열판 없음 1.0 / 인증값 없는 조합 0.9 */
  readonly combinationCorrection?: number;
  /** Kv — 액상 점도가 100 cP 이하이면 1.0 (C.2.2.1) */
  readonly viscosityCorrection?: number;
  /**
   * ηc 를 근사식 (C.15)로 구할지 여부. 기본은 false —
   * 정의식 (C.14)를 수치해로 푼다(Figure C.1 곡선이 곧 이 식이다).
   */
  readonly useApproximateCriticalRatio?: boolean;
}

export type TwoPhaseFlowRegime = "critical" | "subcritical";

export interface TwoPhaseSizingSuccess {
  readonly ok: true;
  readonly requiredAreaMm2: number;
  readonly flowRegime: TwoPhaseFlowRegime;
  readonly equation: "API 520 Part I (C.21)";
  readonly intermediates: {
    /** ω — 오메가 파라미터, 식 (C.12) */
    readonly omega: number;
    /** ηc — 임계 압력비 */
    readonly criticalPressureRatio: number;
    readonly criticalRatioEquation:
      | "API 520 Part I (C.14)"
      | "API 520 Part I (C.15)";
    /** Pcf — 임계 압력 (kPa 절대), 식 (C.13) */
    readonly criticalPressureKpaAbs: number;
    /** ηa = P2/P1 — 실제 배압비 */
    readonly backpressureRatio: number;
    /** G — 질량유속 (kg/s·m²), 식 (C.18) 또는 (C.19) */
    readonly massFlux: number;
    readonly massFluxEquation:
      | "API 520 Part I (C.18)"
      | "API 520 Part I (C.19)";
    readonly kd: number;
    readonly kb: number;
    readonly kc: number;
    readonly kv: number;
  };
  readonly warnings: readonly string[];
}

export interface TwoPhaseSizingFailure {
  readonly ok: false;
  readonly reason: "invalid-input" | "no-solution";
  readonly message: string;
}

export type TwoPhaseSizingResult =
  | TwoPhaseSizingSuccess
  | TwoPhaseSizingFailure;

/**
 * 오메가 파라미터 — 식 (C.12).  ω = 9·(v9/v1 − 1)
 */
export function omegaParameter(
  inletSpecificVolume: number,
  flashSpecificVolume: number,
): number {
  return 9 * (flashSpecificVolume / inletSpecificVolume - 1);
}

/**
 * 임계 압력비 ηc 의 근사식 — 식 (C.15).
 *
 *   ηc = [ 1 + (1.0446 − 0.0093431·ω^0.5)·ω^(−0.56261) ] ^ (−0.70356 + 0.014685·ln ω)
 */
export function criticalPressureRatioApprox(omega: number): number {
  const base =
    1 +
    (1.0446 - 0.0093431 * Math.pow(omega, 0.5)) * Math.pow(omega, -0.56261);
  const exponent = -0.70356 + 0.014685 * Math.log(omega);
  return Math.pow(base, exponent);
}

/**
 * 식 (C.14) 의 좌변. 근을 찾으면 그것이 ηc 다.
 *
 *   f(ηc) = ηc² + (ω²−2ω)(1−ηc)² + 2ω²·ln ηc + 2ω²(1−ηc)
 *
 * η → 0⁺ 에서 ln 항 때문에 −∞, η = 1 에서 +1 이므로 (0,1) 에 근이 존재한다.
 */
export function criticalRatioResidual(omega: number, eta: number): number {
  const w2 = omega * omega;
  return (
    eta * eta +
    (w2 - 2 * omega) * (1 - eta) * (1 - eta) +
    2 * w2 * Math.log(eta) +
    2 * w2 * (1 - eta)
  );
}

/**
 * 임계 압력비 ηc 를 정의식 (C.14)의 수치해로 구한다.
 * 근사식 (C.15)를 초기값으로 삼아 이분법으로 좁힌다 — 단조·유계라 항상 수렴한다.
 */
export function criticalPressureRatioExact(omega: number): number | null {
  let lo = 1e-9;
  let hi = 1 - 1e-12;
  if (criticalRatioResidual(omega, lo) > 0) return null;
  if (criticalRatioResidual(omega, hi) < 0) return null;

  for (let i = 0; i < 200; i += 1) {
    const mid = (lo + hi) / 2;
    if (criticalRatioResidual(omega, mid) < 0) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/**
 * 임계 질량유속 G — 식 (C.18) SI.  G = ηc·√(P1/(v1·ω))
 * @param p1Pa P1 (Pa 절대)
 */
export function criticalMassFlux(
  criticalRatio: number,
  p1Pa: number,
  v1: number,
  omega: number,
): number {
  return criticalRatio * Math.sqrt(p1Pa / (v1 * omega));
}

/**
 * 아임계 질량유속 G — 식 (C.19) SI.
 *
 *   G = { −2·[ ω·ln ηa + (ω−1)(1−ηa) ] }^(1/2) / ( ω·(1/ηa − 1) + 1 ) · √(P1/v1)
 */
export function subcriticalMassFlux(
  omega: number,
  etaA: number,
  p1Pa: number,
  v1: number,
): number | null {
  const inner = -2 * (omega * Math.log(etaA) + (omega - 1) * (1 - etaA));
  if (inner < 0) return null;
  const denominator = omega * (1 / etaA - 1) + 1;
  return (Math.sqrt(inner) / denominator) * Math.sqrt(p1Pa / v1);
}

function invalid(message: string): TwoPhaseSizingFailure {
  return { ok: false, reason: "invalid-input", message };
}

/** 2상 유동 소요 배출 면적을 계산한다 (C.2.2). */
export function sizeTwoPhase(
  input: TwoPhaseSizingInput,
): TwoPhaseSizingResult {
  const {
    requiredFlowKgH: W,
    relievingPressureKpaAbs: P1kPa,
    backpressureKpaAbs: P2kPa,
    inletSpecificVolumeM3Kg: v1,
    flashSpecificVolumeM3Kg: v9,
    valveType,
  } = input;

  const positives: ReadonlyArray<readonly [string, number]> = [
    ["소요 유량 W", W],
    ["입구 압력 P1", P1kPa],
    ["입구 비체적 v1", v1],
    ["플래시 비체적 v9", v9],
  ];
  for (const [label, v] of positives) {
    if (!Number.isFinite(v) || v <= 0) {
      return invalid(`${label} 은(는) 0보다 큰 유한한 값이어야 합니다.`);
    }
  }
  if (!Number.isFinite(P2kPa) || P2kPa < 0) {
    return invalid("배압 P2 는 0 이상이어야 합니다.");
  }
  if (P2kPa >= P1kPa) {
    return invalid(
      "배압 P2 가 입구 압력 P1 이상입니다. 이 조건에서는 유동이 발생하지 않습니다.",
    );
  }
  if (v9 <= v1) {
    return invalid(
      "P1 의 90 % 에서의 비체적 v9 가 입구 비체적 v1 이하입니다. " +
        "감압하면 비체적은 커져야 하므로 플래시 계산 결과를 확인하십시오.",
    );
  }

  const Kd = input.dischargeCoefficient ?? KD_TWO_PHASE_PRELIMINARY;
  const Kc = input.combinationCorrection ?? 1.0;
  const Kv = input.viscosityCorrection ?? 1.0;
  for (const [label, v] of [
    ["Kd", Kd],
    ["Kc", Kc],
    ["Kv", Kv],
  ] as const) {
    if (!Number.isFinite(v) || v <= 0) return invalid(`${label} 가 올바르지 않습니다.`);
  }

  const warnings: string[] = [];

  // ── Kb: 벨로우즈에만 적용 ────────────────────────────────────
  let Kb = 1.0;
  if (valveType === "balanced-bellows") {
    if (input.bellowsBackpressureCorrection === undefined) {
      warnings.push(
        "밸런스드 벨로우즈인데 배압 보정계수 Kb 가 입력되지 않아 1.0 으로 계산했습니다. " +
          "제작사 자료(또는 Figure 31)에서 확인해 입력해야 합니다.",
      );
    } else {
      Kb = input.bellowsBackpressureCorrection;
      if (!Number.isFinite(Kb) || Kb <= 0 || Kb > 1) {
        return invalid("Kb 는 0 초과 1 이하이어야 합니다.");
      }
    }
  } else if (input.bellowsBackpressureCorrection !== undefined) {
    warnings.push(
      "일반형·파일럿형에는 배압 보정계수 Kb 가 적용되지 않습니다. 1.0 으로 계산했습니다.",
    );
  }

  // 배압이 설정압의 10 % 를 넘으면 벨로우즈를 검토하라는 예제 주석(C.2.2.2.2) 반영
  if (valveType === "conventional" && P2kPa / P1kPa > 0.1) {
    warnings.push(
      "배압비가 10 % 를 넘습니다. 밸런스드 벨로우즈 또는 파일럿형 PRV 적용을 검토하십시오(5.11.3).",
    );
  }

  // ── Step 1: ω (식 C.12) ─────────────────────────────────────
  const omega = omegaParameter(v1, v9);

  // ── Step 2: 임계 판별 (식 C.13~C.15) ────────────────────────
  const useApprox = input.useApproximateCriticalRatio === true;
  const etaC = useApprox
    ? criticalPressureRatioApprox(omega)
    : criticalPressureRatioExact(omega);

  if (etaC === null || !Number.isFinite(etaC) || etaC <= 0 || etaC >= 1) {
    return {
      ok: false,
      reason: "no-solution",
      message:
        `오메가 ω = ${omega.toFixed(4)} 에서 임계 압력비를 구할 수 없습니다. ` +
        "비체적 입력값을 확인하십시오.",
    };
  }

  const PcfKpa = etaC * P1kPa;
  const etaA = P2kPa / P1kPa;
  const regime: TwoPhaseFlowRegime = PcfKpa >= P2kPa ? "critical" : "subcritical";

  // ── Step 3: 질량유속 (식 C.18 / C.19) ───────────────────────
  const P1Pa = P1kPa * 1000;
  let G: number | null;
  let massFluxEquation: TwoPhaseSizingSuccess["intermediates"]["massFluxEquation"];

  if (regime === "critical") {
    G = criticalMassFlux(etaC, P1Pa, v1, omega);
    massFluxEquation = "API 520 Part I (C.18)";
  } else {
    G = subcriticalMassFlux(omega, etaA, P1Pa, v1);
    massFluxEquation = "API 520 Part I (C.19)";
  }

  if (G === null || !Number.isFinite(G) || G <= 0) {
    return {
      ok: false,
      reason: "no-solution",
      message:
        "질량유속이 계산되지 않습니다. 오메가 파라미터와 배압비 조합을 확인하십시오.",
    };
  }

  // ── Step 4: 소요 면적 (식 C.21) ─────────────────────────────
  const requiredAreaMm2 = (277.8 * W) / (Kd * Kb * Kc * Kv * G);

  return {
    ok: true,
    requiredAreaMm2,
    flowRegime: regime,
    equation: "API 520 Part I (C.21)",
    intermediates: {
      omega,
      criticalPressureRatio: etaC,
      criticalRatioEquation: useApprox
        ? "API 520 Part I (C.15)"
        : "API 520 Part I (C.14)",
      criticalPressureKpaAbs: PcfKpa,
      backpressureRatio: etaA,
      massFlux: G,
      massFluxEquation,
      kd: Kd,
      kb: Kb,
      kc: Kc,
      kv: Kv,
    },
    warnings,
  };
}
